import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAtom } from "jotai";

import { SCROLL_CONTAINER_ID } from "@arc/shared/constants/ui";
import { catalogScrollPositionAtom } from "@/atoms/ui";
import { CatalogRoute } from "@/constants/routes";

interface ScrollOptions {
  behavior?: ScrollBehavior;
  preventMomentaryFlash?: boolean;
}

export const useCatalogScrollPosition = () => {
  const location = useLocation();
  const [scrollPosition, setScrollPosition] = useAtom(
    catalogScrollPositionAtom
  );

  const isRestoringRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const previousLocationRef = useRef(location.pathname);
  const navigatingToCatalogRef = useRef(false);
  const shouldRestoreScrollRef = useRef(false);

  const getScrollContainer = useCallback(
    () => document.getElementById(SCROLL_CONTAINER_ID),
    []
  );

  const getCurrentScrollPosition = useCallback(() => {
    const scrollContainer = getScrollContainer();
    return scrollContainer ? scrollContainer.scrollTop : 0;
  }, [getScrollContainer]);

  const isInCatalogRelatedPage = useCallback(
    () =>
      Object.values(CatalogRoute).some((route) =>
        location.pathname.startsWith(route)
      ),
    [location.pathname]
  );

  const setScrollToPositionInstantly = useCallback(
    (position: number) => {
      const scrollContainer = getScrollContainer();
      if (scrollContainer && position > 0) {
        const originalScrollBehavior = scrollContainer.style.scrollBehavior;
        scrollContainer.style.scrollBehavior = "auto";

        scrollContainer.scrollTop = position;

        setTimeout(() => {
          scrollContainer.style.scrollBehavior = originalScrollBehavior;
        }, 100);
      }
    },
    [getScrollContainer]
  );

  const setScrollToPosition = useCallback(
    (position: number, options: ScrollOptions = {}) => {
      const { behavior = "auto", preventMomentaryFlash = false } = options;
      const scrollContainer = getScrollContainer();

      if (scrollContainer && position > 0) {
        isRestoringRef.current = true;

        if (preventMomentaryFlash) {
          setScrollToPositionInstantly(position);
        } else {
          scrollContainer.scrollTo({
            top: position,
            behavior,
          });
        }

        const restorePosition = () => {
          if (scrollContainer) {
            scrollContainer.scrollTop = position;
          }
        };

        setTimeout(restorePosition, 50);
        setTimeout(restorePosition, 150);
        setTimeout(() => {
          restorePosition();
          isRestoringRef.current = false;
        }, 300);
      }
    },
    [getScrollContainer, setScrollToPositionInstantly]
  );

  const saveScrollPosition = useCallback(() => {
    if (!isRestoringRef.current) {
      const currentScrollPosition = getCurrentScrollPosition();
      if (currentScrollPosition > 0) {
        setScrollPosition(currentScrollPosition);
      }
    }
  }, [getCurrentScrollPosition, setScrollPosition]);

  const restoreScrollPosition = useCallback(
    (options: ScrollOptions = {}) => {
      if (scrollPosition > 0) {
        setScrollToPosition(scrollPosition, options);
      }
    },
    [scrollPosition, setScrollToPosition]
  );

  const prepareForBackNavigation = useCallback(() => {
    if (isInCatalogRelatedPage() && scrollPosition > 0) {
      navigatingToCatalogRef.current = true;
      shouldRestoreScrollRef.current = true;

      const scrollContainer = getScrollContainer();
      if (scrollContainer) {
        scrollContainer.style.scrollBehavior = "auto";
      }

      return true;
    }
    return false;
  }, [getScrollContainer, isInCatalogRelatedPage, scrollPosition]);

  const shouldPreventScrollToTop = useCallback(
    () => location.pathname === CatalogRoute.index && scrollPosition > 0,
    [location.pathname, scrollPosition]
  );

  useLayoutEffect(() => {
    if (
      isInitialMountRef.current &&
      location.pathname === CatalogRoute.index &&
      scrollPosition > 0
    ) {
      setScrollToPositionInstantly(scrollPosition);
      isInitialMountRef.current = false;
    }
  }, [location.pathname, scrollPosition, setScrollToPositionInstantly]);

  useEffect(() => {
    if (
      location.pathname !== previousLocationRef.current &&
      location.pathname === CatalogRoute.index &&
      shouldRestoreScrollRef.current &&
      scrollPosition > 0 &&
      navigatingToCatalogRef.current
    ) {
      setScrollToPositionInstantly(scrollPosition);

      setTimeout(() => {
        navigatingToCatalogRef.current = false;
        shouldRestoreScrollRef.current = false;
      }, 100);
    }

    previousLocationRef.current = location.pathname;
  }, [location.pathname, scrollPosition, setScrollToPositionInstantly]);

  return {
    scrollPosition,
    saveScrollPosition,
    restoreScrollPosition,
    getCurrentScrollPosition,
    setScrollToPosition,
    setScrollToPositionInstantly,
    prepareForBackNavigation,
    shouldPreventScrollToTop,
    isInCatalogRelatedPage,
  };
};
